import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { type BaseStitchListState } from '@modules/common/types/base-stitch-list-state';
import { KpCountBadgeComponent } from '@modules/knowledge/components/kp-count-badge/kp-count-badge.component';
import { StitchTypeContextMenuDirective } from '@modules/knowledge/directives/stitch-type-context-menu.directive';
import { KpResultCountsService } from '@modules/knowledge/services/kp-result-counts.service';
import { type KPListType } from '@modules/knowledge/types/kp-list-type';
import { getKPResultType, KP_RESULT_TYPES, type KPResultCounts } from '@modules/knowledge/types/kp-result-types';
import { type StateKey } from '@modules/settings/types/state-key';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { map, startWith, switchMap } from 'rxjs/operators';

type FilteredListState = Pick<
  BaseStitchListState<StitchItemSummaryDto, unknown>,
  'changes' | 'hasActiveFilters' | 'clearFilters'
>;

/**
 * The row above a Knowledge Panel result list: the result type button with its menu, the section's sort
 * button (projected), and `Clear filters`.
 */
@Component({
  selector: 'app-kp-results-header',
  templateUrl: './kp-results-header.component.html',
  styleUrls: ['./kp-results-header.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Button2Component, IconComponent, KpCountBadgeComponent, MatTooltipModule, StitchTypeContextMenuDirective],
  host: {
    class: 'block',
  },
})
export class KpResultsHeaderComponent {
  public readonly type = input.required<KPListType>();
  public readonly stateKey = input.required<StateKey>();
  /** The list state behind the projected sort button. */
  public readonly listState = input.required<FilteredListState>();
  /** The result count of the list below, as the list reports it. */
  public readonly count = input<number>(0);
  /** Disables the type button. */
  public readonly disabled = input<boolean>(false);

  public readonly typeChange = output<KPListType>();

  private readonly resultCounts = inject(KpResultCountsService);

  protected readonly menuOpened = signal(false);
  protected readonly entry = computed(() => getKPResultType(this.type()) ?? KP_RESULT_TYPES[0]);
  protected readonly menuCounts = computed<KPResultCounts>(() => ({
    ...this.resultCounts.counts(),
    [this.entry().type]: this.count(),
  }));
  /** `hasActiveFilters()` reads plain fields, so it is re-read on every change of the state. */
  protected readonly filtersActive = toSignal(
    toObservable(this.listState).pipe(
      switchMap((state) =>
        state.changes().pipe(
          startWith(undefined),
          map(() => state.hasActiveFilters()),
        ),
      ),
    ),
    { initialValue: false },
  );

  constructor() {
    inject(DestroyRef).onDestroy(() => this.resultCounts.close());
  }

  protected handleMenuOpened(opened: boolean): void {
    this.menuOpened.set(opened);
    if (opened) {
      this.resultCounts.open(this.entry().type);
    } else {
      this.resultCounts.close();
    }
  }

  protected clearFilters(): void {
    this.listState().clearFilters();
  }
}
